import React, { useCallback, useEffect, useState } from 'react';
import ReportIcon from './ReportIcons';
import { downloadReportV5PDF } from '../../api/reports';
import './ReportShell.css';

/**
 * 报告页外壳：左侧「电梯式」目录 + 固定在目录底部的操作区 + 右下返回顶部。
 * 基础版（ReportV5）与专业版（ReportV5Detailed）共用。
 *
 * printMode（Playwright PDF 印刷模式）直接透传 children，不渲染任何交互 chrome，
 * 保证 PDF 长图与改版前一致。
 *
 * props:
 *  - sections: [{ id, label }]  目录条目；id 必须与正文 <section id> 一一对应（scroll-spy 靠它）
 *  - footer:   ReactNode        固定在目录底部的内容（下载按钮 / 引导文字），列表滚动时不动
 *  - printMode boolean
 *  - tocTitle  string           目录标题，默认「报告目录」
 */
export default function ReportShell({
  sections = [],
  footer = null,
  printMode = false,
  tocTitle = '报告目录',
  children,
}) {
  if (printMode) return <>{children}</>;
  return (
    <div className="rpt-shell">
      <ReportToc sections={sections} footer={footer} title={tocTitle} />
      <div className="rpt-main">{children}</div>
      <BackToTop />
    </div>
  );
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

function ReportToc({ sections, footer, title }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || null);

  // scroll-spy：哪个 section 进入视口上半区就高亮对应目录项。
  // rootMargin 上 -20% / 下 -70% → 触发带集中在视口上方约 1/4 处，跳转/滚动手感稳定。
  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!els.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const handleJump = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  }, []);

  return (
    <aside className="rpt-toc">
      <div className="rpt-toc-head">
        <ReportIcon name="list" size={15} />
        {title}
      </div>
      <nav className="rpt-toc-list" aria-label={title}>
        {sections.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`rpt-toc-item${activeId === s.id ? ' active' : ''}`}
            aria-current={activeId === s.id ? 'true' : undefined}
            onClick={() => handleJump(s.id)}
          >
            <span className="rpt-toc-dot" />
            <span className="rpt-toc-idx">{String(i + 1).padStart(2, '0')}</span>
            <span className="rpt-toc-label">{s.label}</span>
          </button>
        ))}
      </nav>
      {footer && <div className="rpt-toc-foot">{footer}</div>}
    </aside>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      type="button"
      className={`rpt-backtop${show ? ' show' : ''}`}
      aria-label="返回顶部"
      onClick={() => window.scrollTo({ top: 0, behavior: scrollBehavior() })}
    >
      <ReportIcon name="arrow-up" size={20} />
    </button>
  );
}

// 目录底部下载按钮：基础版 variant=rough / 专业版 variant=detailed 共用（沿用原 PNG 长图下载逻辑）
export function ReportDownloadButton({ assessmentId, variant }) {
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const previewMode = !assessmentId || assessmentId === 'preview' || assessmentId === 'demo';

  const handleDownload = async () => {
    if (previewMode || busy) return;
    setBusy(true);
    setErrMsg(null);
    const res = await downloadReportV5PDF(assessmentId, { variant });
    setBusy(false);
    if (!res.ok) setErrMsg(res.error || '报告下载失败');
  };

  return (
    <>
      <button
        type="button"
        className="rpt-dl-btn"
        disabled={previewMode || busy}
        title={previewMode ? '预览模式不支持下载（无真实测评 id）' : ''}
        onClick={handleDownload}
      >
        <ReportIcon name="download" size={16} />
        {busy ? '正在生成报告…' : '下载报告（PDF）'}
      </button>
      {errMsg && <span className="rpt-dl-error">{errMsg}</span>}
    </>
  );
}

// 引导文字（仅基础版用）：指向正文末尾的「查看专业版报告」CTA，本身不可点击，真正查看走那张卡片。
export function ReportProHint() {
  return (
    <p className="rpt-pro-hint">
      <ReportIcon name="lightbulb" size={14} />
      <span>想了解更深入的分析？可在末尾处<b>查看专业版报告</b></span>
    </p>
  );
}
